import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { gsap } from "gsap";
import { Container } from "pixi.js";

import { IReelData } from "../../../abstracts/elements/AbstractReel";
import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import { EventsManager, ReelsEvents, WinEvents } from "../../../abstracts/events/EventsManager";
import { getIsReelsLandingSkipped } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { ScatterStates, ShakeRepeat, TimelineLabels } from "../../../abstracts/types/enums";
import { IScatterDecoratorData, ShakeProps } from "../../../abstracts/types/interfaces";
import { isAllTrue } from "../../../abstracts/utils/ArrayUtils";
import { generateFilledArray } from "../../../abstracts/utils/JSUtils";
import { playSound } from "../../../abstracts/utils/SoundUtils";
import { DDD } from "../../DDD";
import {
    DDDSoundNames,
    reelLandingSounds,
    scatterLandingSounds,
} from "../../assetsLoader/DDDAssetsNames";
import {
    davidPanningReelStop,
    normalConfig,
    reelsConfig,
    xGodAnticipationRepeat,
} from "../../config/DDDConfig";
import DDDReel from "../../modules/Reels/DDDReel";
import { DDDReelsView } from "../../modules/Reels/DDDReelsView";
import { DDDScatterSymbol } from "../../modules/Reels/symbols/DDDScatterSymbol";
import { DDDSymbol } from "../../modules/Reels/symbols/DDDSymbol";
import { DDDWildSymbol } from "../../modules/Reels/symbols/DDDWildSymbol";
import { DDDSpecialSymbolNames, DDDSymbolNames } from "../../types/DDDEnums";

let xGodAnticipationCounter = 0;

export const DDDResultHandler = (data: IScatterDecoratorData): void => {
    const {
        initialDataReels = DDD.model.gameData.reels,
        finalDataReels = DDD.model.gameData.evaluatedArea,
        restrictedReelsByIndex = [0, 1],
        scatterSymbolName = DDDSpecialSymbolNames.SCATTER,
        gameReels = [],
        timeline = gsap.timeline(),
        nearWinDuration = normalConfig.nearWinDuration,
        stopSpinDelay = 0,
        wasFeatureBuy = DDD.model.gameData.wasFeatureBuy || DDD.model.data.boostedBet,
        minScattersToTriggerFeature = 3,
        reelsContainer = new Container(),
        onDecoratorComplete = () => {},
    } = data;

    const minScattersToTriggerNearWin: number = minScattersToTriggerFeature - 1;

    let scattersCounter: number = 0;

    let nearWinOnReels: boolean[] = [];
    let xGodAnticipationOnReels: boolean[] = [];

    const gameScatters: (DDDScatterSymbol | null)[] = [null, null, null, null, null];

    initialDataReels.forEach((symbols: string[], reelIndex: number) => {
        let hasNearWin: boolean = false;

        if (scattersCounter >= minScattersToTriggerNearWin) {
            if (!restrictedReelsByIndex.includes(reelIndex)) {
                hasNearWin = true;
            }
        }

        let symbolsIncludeScatter: boolean = false;
        symbols.forEach((symbolName: string) => {
            if (symbolName === scatterSymbolName) {
                symbolsIncludeScatter = true;
            }
        });

        if (symbolsIncludeScatter) {
            scattersCounter++;
        }

        nearWinOnReels[reelIndex] =
            hasNearWin && (!wasFeatureBuy || DDD.model.isLuckyDraw || DDD.model.isXbet);

        if (DDD.model.xGodMap[reelIndex] && xGodAnticipationCounter === xGodAnticipationRepeat) {
            if (!nearWinOnReels[reelIndex]) {
                xGodAnticipationOnReels[reelIndex] = true;
            }

            nearWinOnReels[reelIndex] = true;

            xGodAnticipationCounter = 0;
        } else if (
            DDD.model.xGodMap[reelIndex] &&
            xGodAnticipationCounter < xGodAnticipationRepeat
        ) {
            xGodAnticipationCounter++;
        }
    });

    timeline.addLabel(TimelineLabels.SCATTER_DECORATOR_START, `>`);

    const stoppedReels: boolean[] = generateFilledArray(reelsConfig.reelsNumber, false);
    let scatterState: ScatterStates = ScatterStates.NORMAL;

    const wildSymbols: (DDDSymbol | null)[][] = generateFilledArray(reelsConfig.reelsNumber, []);

    let firstNearWinToStop: boolean = true;

    let totalScattersLanded: number = 0;
    let winningScatters: DDDScatterSymbol[] = [];
    let totalReelsLanded: number = 0;
    let reelLandingSkipEnabled: boolean = false;

    // * this is the start of the landing function
    const onReelLand = (reelId: number): void => {
        totalReelsLanded++;

        const reel = gameReels[reelId] as DDDReel;
        const visibleSymbols = reel.getAllVisibleStopSymbols();

        if (!reelLandingSkipEnabled) {
            playSound({
                name: reelLandingSounds[reelId],
                stereo: davidPanningReelStop[reelId],
            });
        }

        if (SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) || getIsReelsLandingSkipped()) {
            reelLandingSkipEnabled = true;
        }
        // }

        if (DDD.model.xNudgeMap[reelId]) {
            EventsManager.trigger(WinEvents.SHAKE_NORMAL_SCREEN, {
                intensity: 6,
            } as ShakeProps);
        }

        if (DDD.model.xGodMap[reelId]) {
            if (DDD.model.fullXGodReelIndex === reelId) {
                playSound({ name: DDDSoundNames.XGOD_LAND_FULL });
            } else {
                playSound({ name: DDDSoundNames.XGOD_LAND_PARTIAL });
            }
        }

        if (DDD.model.gameData.betWayWins.length > 0 && reelId >= 2) {
            let winBlinkPlayed: boolean = false;

            const isSymbolXnudge = (symbol: AbstractSymbol) => {
                return (
                    symbol.name === DDDSymbolNames.XN_1 ||
                    symbol.name === DDDSymbolNames.XN_2 ||
                    symbol.name === DDDSymbolNames.XN_3
                );
            };

            // post-release code
            DDD.model.gameData.betWayWins.forEach((betWay) => {
                const symbolName = betWay.symbol;
                visibleSymbols.forEach((symbol: AbstractSymbol, symbolIndex) => {
                    if (DDD.model.winningPos[reel.reelId][symbolIndex] && reelId > 1) {
                        if (
                            symbol.name === symbolName ||
                            symbol.isWild ||
                            (isSymbolXnudge(symbol) &&
                                (gameReels[reelId - 1] as DDDReel).xNudgeSymbol === symbol)
                        ) {
                            const prevWinningSymbols: AbstractSymbol[] = [];

                            for (let prevReelIndex = 0; prevReelIndex < reelId; prevReelIndex++) {
                                const prevVisibleSymbols = (
                                    gameReels[prevReelIndex] as DDDReel
                                ).getAllVisibleStopSymbols();

                                prevVisibleSymbols.forEach((symbol: AbstractSymbol) => {
                                    if (
                                        symbol.name === symbolName ||
                                        symbol.isWild ||
                                        (isSymbolXnudge(symbol) &&
                                            (gameReels[reelId - 1] as DDDReel).xNudgeSymbol ===
                                                symbol)
                                    ) {
                                        if (!prevWinningSymbols.includes(symbol)) {
                                            prevWinningSymbols.push(symbol);
                                        }
                                    }
                                });

                                if (prevWinningSymbols.length >= reelId) {
                                    prevWinningSymbols.forEach((prevSymbol) => {
                                        if (prevSymbol.name === symbolName && reelId === 2) {
                                            prevSymbol.getLandingAnimation();
                                        }
                                    });
                                    symbol.getLandingAnimation();
                                }
                            }

                            if (!winBlinkPlayed) {
                                winBlinkPlayed = true;

                                playSound({
                                    name: DDDSoundNames.WIN_BLINK,
                                });
                            }
                        }
                    }
                });
            });
            // post-release code
        }

        if (initialDataReels[reelId].indexOf(scatterSymbolName) !== -1) {
            totalScattersLanded++;

            if (
                totalScattersLanded === minScattersToTriggerNearWin &&
                (!wasFeatureBuy || DDD.model.isLuckyDraw || DDD.model.isXbet) &&
                reelId !== reelsConfig.reelsNumber - 1
            ) {
                playSound({
                    name: DDDSoundNames.SCATTER_ANTICIPATION,
                });
            }

            if (wasFeatureBuy && !DDD.model.isLuckyDraw && totalScattersLanded > 2) {
                // stopSound({
                //     name: scatterLandingSounds[totalScattersStoppedOnLanding - 2],
                // });
            }

            if (
                !(
                    reelId === reelsConfig.reelsNumber - 1 &&
                    totalScattersLanded <= minScattersToTriggerFeature
                ) ||
                (reelId === reelsConfig.reelsNumber - 1 &&
                    totalScattersLanded === minScattersToTriggerNearWin)
            ) {
                if (!(reelId === 1 && DDD.model.isXbet)) {
                    playSound({
                        name: scatterLandingSounds[totalScattersLanded - 1],
                        stereo: davidPanningReelStop[totalScattersLanded - 1],
                    });
                }
            }
        }

        if (reelId === reelsConfig.reelsNumber - 1) {
            if (totalScattersLanded === minScattersToTriggerNearWin) {
                playSound({ name: DDDSoundNames.SCATTER_ANTICIPATION_FAIL });
            }

            gameReels.forEach((reel, reelIndex) => {
                (reel as DDDReel).getAllVisibleStopSymbols().forEach((symbol, symbolIndex) => {
                    const cond1 =
                        finalDataReels[reelIndex][symbolIndex] === DDDSpecialSymbolNames.WILD;
                    const cond2 = symbol instanceof DDDScatterSymbol;
                    const cond3 = !gameScatters.includes(symbol as DDDScatterSymbol);

                    if (cond1 && cond2 && cond3) {
                        symbol.isWild = true;
                    }

                    if (cond2) {
                        gameScatters[reelIndex] = symbol;
                    }
                });
            });
        }

        if (
            reelId === reelsConfig.reelsNumber - 1 &&
            totalScattersLanded > minScattersToTriggerNearWin &&
            (!wasFeatureBuy || DDD.model.isLuckyDraw)
        ) {
            playSound({ name: DDDSoundNames.SCATTER_ANTICIPATION_WIN });
        }

        //////////////////handle scatters win animation
        if (initialDataReels[reelId].indexOf(scatterSymbolName) !== -1) {
            if (totalScattersLanded === minScattersToTriggerFeature - 1) {
                if (reelId !== reelsConfig.reelsNumber - 1) {
                    scatterState = ScatterStates.ANTICIPATION;
                }
            }

            if (totalScattersLanded >= minScattersToTriggerFeature - 1) {
                let shakeIntensity = 6;
                let shakeRepeat = ShakeRepeat.ONE;

                if (totalScattersLanded === 3) {
                    shakeIntensity = 12;
                    shakeRepeat = ShakeRepeat.THREE;
                } else if (totalScattersLanded === 4) {
                    shakeIntensity = 18;
                    shakeRepeat = ShakeRepeat.FIVE;
                }
                EventsManager.trigger(WinEvents.SHAKE_NORMAL_SCREEN, {
                    intensity: shakeIntensity,
                    repeat: shakeRepeat,
                } as ShakeProps);
            }
        }

        if (
            totalReelsLanded === reelsConfig.reelsNumber &&
            winningScatters.length <= minScattersToTriggerNearWin
        ) {
            if (winningScatters.length === minScattersToTriggerNearWin) {
                scatterState = ScatterStates.LOSE;
            } else {
                scatterState = ScatterStates.NORMAL;
            }
        } else if (winningScatters.length >= 3 && totalReelsLanded === reelsConfig.reelsNumber) {
            scatterState = ScatterStates.WIN;
        }

        if (
            DDD.model.wildMap[reelId] &&
            initialDataReels[reelId].includes(DDDSpecialSymbolNames.WILD)
        ) {
            playSound({ name: DDDSoundNames.WILD_LAND });
        }

        if (
            DDD.model.data.mode === AbstractGameMode.FREESPIN &&
            DDD.model.data.nextMode === AbstractGameMode.SUPER_FREESPIN &&
            finalDataReels[reelId].includes(DDDSpecialSymbolNames.SCATTER)
        ) {
            playSound({
                name: DDDSoundNames.SCATTER_LANDING_4,
            });
        }

        //////////////////
    };
    // * this is the end of the landing function

    // * this is the start of onReelSpinComplete function
    const onReelSpinComplete = (reelId: number): void => {
        stoppedReels[reelId] = true;

        if (initialDataReels[reelId].indexOf(scatterSymbolName) !== -1) {
            const scatterSymbol = (reelsContainer as DDDReelsView).fakeScatters[reelId];
            if (scatterSymbol !== undefined) {
                winningScatters.push(scatterSymbol);
            }
        }

        if (reelId < reelsConfig.reelsNumber - 1) {
            if (winningScatters.length >= minScattersToTriggerNearWin) {
                scatterState = ScatterStates.ANTICIPATION;
            } else if (winningScatters.length < minScattersToTriggerNearWin) {
                scatterState = ScatterStates.NORMAL;
            }
        } else {
            if (winningScatters.length === minScattersToTriggerNearWin) {
                scatterState = ScatterStates.LOSE;
            } else if (winningScatters.length >= minScattersToTriggerFeature) {
                scatterState = ScatterStates.WIN;
            } else if (
                DDD.model.gameData.mode === AbstractGameMode.FREESPIN &&
                DDD.model.gameData.nextMode === AbstractGameMode.SUPER_FREESPIN &&
                winningScatters.length === 1
            ) {
                scatterState = ScatterStates.WIN;
            }
        }

        if (wasFeatureBuy && !DDD.model.isLuckyDraw) {
            if (scatterState === ScatterStates.WIN) {
                if (totalReelsLanded === reelsConfig.reelsNumber) {
                    const winningScattersTimeline: gsap.core.Timeline = gsap.timeline({
                        autoRemoveChildren: true,
                        smoothChildTiming: true,
                        paused: true,
                    });

                    winningScatters.forEach((scatter) => {
                        winningScattersTimeline.add(scatter.getWinAnimation(scatterState), 0);
                    });
                    winningScattersTimeline.resume();
                }
            }
        } else {
            const winningScattersTimeline: gsap.core.Timeline = gsap.timeline({
                autoRemoveChildren: true,
                smoothChildTiming: true,
            });

            winningScatters.forEach((scatter) => {
                let fakeScatterWinAnimation;

                if (
                    DDD.model.gameData.mode === AbstractGameMode.FREESPIN &&
                    DDD.model.gameData.nextMode === AbstractGameMode.SUPER_FREESPIN &&
                    winningScatters.length === 1
                ) {
                    scatter.reset();
                    fakeScatterWinAnimation = scatter.getWinAnimation(scatterState, true, true);

                    let upgradeAnimation = scatter.getUpgradeAnimation();
                    upgradeAnimation.progress(1);

                    const gameScatter = gameScatters[4];
                    if (gameScatter !== null) {
                        gameScatter.reset();

                        gameScatter.getWinAnimation(scatterState, true, true);

                        upgradeAnimation = gameScatter.getUpgradeAnimation();
                        upgradeAnimation.progress(1);
                    }
                } else {
                    fakeScatterWinAnimation = scatter.getWinAnimation(
                        scatterState,
                        scatterState === ScatterStates.WIN &&
                            winningScatters.length > minScattersToTriggerFeature
                    );
                }

                winningScattersTimeline.add(fakeScatterWinAnimation, 0);

                const winAnimationDuration = fakeScatterWinAnimation.duration();

                if (
                    scattersCounter === minScattersToTriggerNearWin &&
                    reelId === reelsConfig.reelsNumber - 1 &&
                    scatterState === ScatterStates.LOSE
                ) {
                    winningScattersTimeline.call(
                        () => {
                            EventsManager.trigger(ReelsEvents.REMOVE_FAKE_SCATTERS);
                        },
                        undefined,
                        winAnimationDuration - 0.3
                    );
                } else if (
                    scattersCounter === 1 &&
                    reelId === reelsConfig.reelsNumber - 1 &&
                    scatterState === ScatterStates.WIN &&
                    DDD.model.gameData.mode === AbstractGameMode.FREESPIN &&
                    DDD.model.gameData.nextMode === AbstractGameMode.SUPER_FREESPIN
                ) {
                    winningScattersTimeline.call(
                        () => {
                            EventsManager.trigger(ReelsEvents.REMOVE_FAKE_SCATTERS);
                        },
                        undefined,
                        winAnimationDuration - 0.3
                    );
                }
            });
        }

        if (isAllTrue(stoppedReels)) {
            if (totalReelsLanded === reelsConfig.reelsNumber) {
                if (totalScattersLanded > minScattersToTriggerNearWin) {
                    playSound({ name: DDDSoundNames.SCATTER_ACTIVATION });
                }
            }

            const revealUnlockedSymbolsTimeline: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    gsap.delayedCall(
                        scattersCounter >= minScattersToTriggerNearWin ||
                            (scattersCounter === 1 &&
                                DDD.model.gameData.mode === AbstractGameMode.FREESPIN &&
                                DDD.model.gameData.nextMode === AbstractGameMode.SUPER_FREESPIN)
                            ? 0.4
                            : 0,
                        () => {
                            EventsManager.trigger(ReelsEvents.REMOVE_FAKE_SCATTERS);

                            onDecoratorComplete();
                        }
                    );
                },
            });

            finalDataReels.forEach((reel, reelIndex) => {
                if (DDD.model.wildMap[reelIndex]) {
                    const gameReel = gameReels[reelIndex] as DDDReel;
                    gameReel.getAllVisibleStopSymbols().forEach((symbol, symbolIndex) => {
                        const gameSymbol = symbol as DDDSymbol;
                        if (symbol.isWild && reel[symbolIndex] === DDDSpecialSymbolNames.WILD) {
                            wildSymbols[reelIndex].push(gameSymbol);
                        }
                    });
                }
            });

            let wildMultiplierIndex = scattersCounter === minScattersToTriggerNearWin ? 1 : 2;
            let wildDelay = scattersCounter === minScattersToTriggerNearWin ? 0.3 : 0.2;

            if (DDD.model.wildMap.filter((isWild) => isWild).length < 2) {
                wildMultiplierIndex = 1;
                wildDelay = 0;
            }

            DDD.model.wildMap.forEach((isWild, wildMapIndex) => {
                let playSoundOnce: boolean = false;
                if (isWild) {
                    const finalDelay =
                        wildDelay *
                        (DDD.model.wildMultipliers[wildMapIndex] - wildMultiplierIndex + 1);

                    wildSymbols[wildMapIndex].forEach((wildSymbol) => {
                        if (wildSymbol instanceof DDDWildSymbol) {
                            revealUnlockedSymbolsTimeline.add(
                                wildSymbol.getMultiplierAnimation(
                                    DDD.model.wildMultipliers[wildMapIndex]
                                ),
                                finalDelay
                            );
                        }

                        ////////
                        if (scattersCounter === minScattersToTriggerNearWin) {
                            if (isWild) {
                                wildSymbols[wildMapIndex].forEach((wildSymbol) => {
                                    if (wildSymbol instanceof DDDScatterSymbol) {
                                        revealUnlockedSymbolsTimeline.add(
                                            wildSymbol.getMultiplierAnimation(
                                                DDD.model.wildMultipliers[wildMapIndex]
                                            ),
                                            finalDelay
                                        );

                                        const fakeScatter = (reelsContainer as DDDReelsView)
                                            .fakeScatters[wildMapIndex];

                                        fakeScatter.isWild = true;
                                    }
                                });

                                const gameScatter = gameScatters[wildMapIndex];
                                if (gameScatter !== null) {
                                    gameScatter.reset();

                                    gameScatter.isWild = true;

                                    gameScatter.getWinAnimation(ScatterStates.LOSE);

                                    revealUnlockedSymbolsTimeline.add(
                                        gameScatter.getMultiplierAnimation(
                                            DDD.model.wildMultipliers[wildMapIndex]
                                        ),
                                        finalDelay
                                    );
                                }
                            }
                        }

                        if (!playSoundOnce && DDD.model.wildMultipliers[wildMapIndex] > 1) {
                            revealUnlockedSymbolsTimeline.call(
                                () => {
                                    playSound({ name: DDDSoundNames.WILD_ADD_MULTI });
                                },
                                undefined,
                                `<`
                            );
                            playSoundOnce = true;
                        }

                        ////////
                    });
                }
            });

            // * handle wilds multipliers start

            // * handle wilds multipliers end

            if (scattersCounter > minScattersToTriggerFeature) {
                gameScatters.forEach((scatter) => {
                    if (scatter !== null) {
                        scatter.getMainSpine().setSkinByName(`sfs`);
                    }
                });
            }
        }
    };
    // * this is the end of onReelSpinComplete function

    gameReels.forEach((reel, reelIndex) => {
        const stopAnimationTimeline = gsap.timeline({});

        let hasScatter: boolean = false;
        initialDataReels[reelIndex].forEach((symbolName: string) => {
            if (symbolName === scatterSymbolName) {
                hasScatter = true;
            }
        });

        const data: IReelData = {
            hasNearWin: nearWinOnReels[reelIndex],
            hasScatter: hasScatter,
            stopSymbolNames: [...initialDataReels[reelIndex]],
            firstScatterReelToStop: false,
        };
        const reelLabel: string = `reelBeginStop${reelIndex}`;

        data.firstScatterReelToStop = hasScatter && firstNearWinToStop;
        if (hasScatter) {
            firstNearWinToStop = false;
        }

        if (nearWinOnReels[reelIndex]) {
            timeline.addLabel(`${TimelineLabels.SHOW_NEAR_WIN_ANIMATION}${reelIndex}`, `>`);

            const reelNearWinAnimation = (reel as DDDReel).getShowNearWinAnimation(
                xGodAnticipationOnReels[reelIndex]
            );

            timeline.add(
                reelNearWinAnimation,
                `${TimelineLabels.SHOW_NEAR_WIN_ANIMATION}${reelIndex}`
            );

            timeline.addLabel(
                reelLabel,
                `${TimelineLabels.SHOW_NEAR_WIN_ANIMATION}${reelIndex}+=${nearWinDuration}`
            );

            const stopReelAnimation = (reel as DDDReel).stopNormalBetAnimation(
                data,
                onReelSpinComplete,
                onReelLand
            );

            stopAnimationTimeline.add(stopReelAnimation, 0);

            timeline.add(stopAnimationTimeline, reelLabel);

            timeline.addLabel(`reelStopped${reelIndex}`);
            timeline.addLabel(
                `${TimelineLabels.HIDE_NEAR_WIN_ANIMATION}${reelIndex}`,
                `reelStopped${reelIndex}`
            );

            timeline.add(
                (reel as DDDReel).getHideNearWinAnimation(xGodAnticipationOnReels[reelIndex]),
                `${TimelineLabels.HIDE_NEAR_WIN_ANIMATION}${reelIndex}`
            );
        } else {
            if (reelIndex > 0) {
                if (nearWinOnReels[reelIndex - 1]) {
                    timeline.addLabel(reelLabel, `>`);
                } else {
                    timeline.addLabel(reelLabel, `reelBeginStop${reelIndex - 1}+=${stopSpinDelay}`);
                }
            } else {
                timeline.addLabel(reelLabel, `>`);
            }

            stopAnimationTimeline.add(
                (reel as DDDReel).stopNormalBetAnimation(data, onReelSpinComplete, onReelLand),
                0
            );

            timeline.add(stopAnimationTimeline, reelLabel);

            timeline.addLabel(`reelStopped${reelIndex}`, `>`);
        }
    });

    // const entries = Object.entries(timeline.labels);
    //
    // const salam = entries.sort((a, b) => {
    //     return timeline.labels[a[0]] - timeline.labels[b[0]];
    // });
    //
    // console.log(salam);

    timeline.addLabel(TimelineLabels.SCATTER_DECORATOR_COMPLETE);
};
